import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Flex,
  Progress,
  message,
  theme,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import AvailabilityIcon from "assets/availability.svg";
import { JOB_STATUS, PartialJobInterface } from "api/jobs/dto";
import dayjs from "dayjs";
import { numberValidator } from "utils/number-validator";
import VacancyLocationCard from "./VacancyLocationForm";
import { storage } from "utils/storage";
import { usePostJobs } from "api/jobs/usePostJob";
import { useUpdateJob } from "api/jobs/useUpdateJob";
import { useNavigate } from "react-router-dom";
import { confirmDayjs } from "utils/date";

const { Title } = Typography;
const { Option } = Select;

type Props = {
  prev: () => void;
  next: () => void;
  formData: PartialJobInterface;
  isEditMode: boolean;
  id?: string;
  setFormData: React.Dispatch<React.SetStateAction<PartialJobInterface>>;
};

const JobDetailsForm: React.FC<Props> = ({
  next,
  formData,
  setFormData,
  prev,
  isEditMode,
  id,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [progress] = useState(0);
  const { token } = theme.useToken();
  const postJobMutation = usePostJobs();
  const updateJobMutation = useUpdateJob();
  const user = storage.getUserFromStorage();
  const navigate = useNavigate();

  const handleChange = (changedValues: PartialJobInterface, allValues: any) => {
    const key = Object.keys(changedValues)[0];

    if (key === "salary" || key === "location" || key === "questions") {
      setFormData({
        ...formData,
        [key]: allValues[key],
      });
    } else {
      setFormData({
        ...formData,
        ...changedValues,
      });
    }
  };

  const handleDraft = () => {
    if (loading) return;
    setLoading(true);

    let deadlineAt = confirmDayjs(formData.deadlineAt);

    const payload = {
      ...formData,
      salary: {
        amount: formData.salary?.amount ?? 0,
        period: formData.salary?.period ?? "",
        isPubliclyAvailable: formData.salary?.isPubliclyAvailable ?? true,
        isNegotiated: formData.salary?.isNegotiated ?? false,
        currency: formData.salary?.currency ?? "NPR",
      },
      status: JOB_STATUS.DRAFT,
      recruiter: user.uid,
      deadlineAt: deadlineAt,
    };

    if (isEditMode && id) {
      updateJobMutation.mutate(
        { id, job: payload },
        {
          onSuccess: () => {
            message.success("Draft updated successfully!");
            setLoading(false);
          },
          onError: () => {
            message.error("Failed to update draft!");
            setLoading(false);
          },
        }
      );
    } else {
      postJobMutation.mutate(payload, {
        onSuccess: (jobRef: any) => {
          message.success("Draft saved successfully!");
          setLoading(false);
          navigate(`/recruiter/update-job/${jobRef.id}`);
        },
        onError: () => {
          message.error("Failed to save draft!");
          setLoading(false);
        },
      });
    }
  };
  const handleSubmit = async () => {
    const values = await form.validateFields();
    const updatedValues = {
      ...values,
      deadlineAt: dayjs(formData.deadlineAt) || null,
    };
    setFormData({ ...formData, ...updatedValues });
    next();
  };

  useEffect(() => {
    form.setFieldsValue({
      salary: formData?.salary ? formData.salary : {},
      deadlineAt: formData?.deadlineAt ? dayjs(formData.deadlineAt) : null,
      questions: formData?.questions ?? [],
    });
  }, [formData, form]);

  return (
    <>
      <Card className="px-8 py-2 mt-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <Title level={2} style={{ fontWeight: 700 }}>
            Add Job Details
          </Title>
          <img alt="#" src={AvailabilityIcon} />
        </div>
      </Card>
      <Card className="mt-6">
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          onValuesChange={handleChange}
        >
          <Row gutter={[48, 32]}>
            <Col xs={24} sm={12} md={12} lg={12} xl={12}>
              <Form.Item
                name={["salary", "amount"]}
                label="Salary"
                rules={[
                  {
                    required: true,
                    message: "This is required",
                  },
                  {
                    validator: numberValidator,
                  },
                ]}
              >
                <Input placeholder="eg. 60000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={12}>
              <Form.Item
                name={["salary", "period"]}
                label="Salary Period"
                rules={[
                  {
                    required: true,
                    message: "This is required",
                  },
                ]}
              >
                <Select placeholder="Select period">
                  <Option value="Daily">Daily</Option>
                  <Option value="Weekly">Weekly</Option>
                  <Option value="Monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Form.Item
                name={["salary", "isPubliclyAvailable"]}
                rules={[
                  {
                    required: false,
                    message: "This is required",
                  },
                ]}
                valuePropName="checked"
              >
                <Checkbox>Show this information publicly</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Form.Item
                name={"deadlineAt"}
                label="Deadline"
                rules={[
                  {
                    required: true,
                    message: "This is required",
                  },
                ]}
              >
                <DatePicker
                  className="w-full"
                  disabledDate={(current) =>
                    current && current.toDate() < new Date()
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <VacancyLocationCard
                formData={formData}
                setFormData={setFormData}
              />
            </Col>
          </Row>

          {/* Questions section */}
          <Row gutter={[48, 32]} className="mt-6">
            <Col span={24}>
              <Title level={4} style={{ fontWeight: 600 }}>
                Screening Questions
              </Title>
              <Form.List name="questions">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Row key={field.key} gutter={16} align="middle">
                        {/* Question text */}
                        <Col xs={24} sm={12} md={10}>
                          <Form.Item
                            {...field}
                            name={[field.name, "question"]}
                            label="Question"
                            rules={[
                              {
                                required: true,
                                message: "Please enter a question",
                              },
                            ]}
                          >
                            <Input placeholder="e.g. How many years of experience?" />
                          </Form.Item>
                        </Col>

                        {/* Field type: text vs yes/no */}
                        <Col xs={24} sm={8} md={6}>
                          <Form.Item
                            {...field}
                            name={[field.name, "type"]}
                            label="Field Type"
                            rules={[
                              { required: true, message: "Select a type" },
                            ]}
                          >
                            <Select placeholder="Select type">
                              <Option value="string">Text</Option>
                              <Option value="boolean">Yes/No</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* Optional / required */}
                        <Col xs={24} sm={8} md={4}>
                          <Form.Item
                            {...field}
                            name={[field.name, "isOptional"]}
                            valuePropName="checked"
                            label="Optional"
                          >
                            <Checkbox>Skip-able</Checkbox>
                          </Form.Item>
                        </Col>

                        {/* Remove button */}
                        <Col xs={24} sm={4} md={4}>
                          <Button
                            type="link"
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    {/* Add new question */}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        + Add Question
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Col>
          </Row>

          <div className="flex gap-x-2 md:justify-between pt-12">
            <Button
              className="h-12 px-8 border-2 border-border text-base flex items-center gap-2"
              onClick={prev}
            >
              <ArrowLeftOutlined />
              Back
            </Button>
            <div className="flex gap-x-5">
              <Flex gap="small" vertical>
                <Button
                  variant="dashed"
                  icon={<InfoCircleOutlined />}
                  disabled={loading}
                  onClick={handleDraft}
                  className="h-12 w-36 "
                >
                  {loading ? (
                    <Progress
                      percent={progress}
                      strokeColor={token.colorPrimary}
                      showInfo={false}
                      type="line"
                    />
                  ) : (
                    "Save Draft"
                  )}
                </Button>
              </Flex>
              <Button
                className="h-12 px-8 border-2 border-border text-base flex items-center gap-2 bg-primary text-white border-none"
                htmlType="submit"
                type="primary"
              >
                Continue
                <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default JobDetailsForm;
